import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import React, { forwardRef, ForwardedRef, ReactElement, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { CellClickedEvent, ColumnState, FirstDataRenderedEvent, GridReadyEvent, RowClickedEvent, EditableCallbackParams, CellKeyDownEvent, ColDef, CellDoubleClickedEvent, PostSortRowsParams, CellMouseDownEvent, CellMouseOverEvent } from 'ag-grid-community';
import { createSaveColumnState, getRowStyle, onGridReady, restoreGridScrollState, saveGridScrollState, withScrollRestoreLoading } from 'src/utils/grid/gridStyleUtils';
import { HandledProps } from 'src/components/common/common';
import { useGridCellSelection } from 'src/utils/grid/GridHandlerContent';
import { ShiftRangeSelectionConfig, useShiftRangeRowSelection } from 'src/utils/grid/useShiftRangeRowSelection';
import { RaonColDef } from 'src/types/grid';
import { GridDropDownForm } from '../form/dropdown_custom/GridDropDownForm';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';

interface GridExcelProps<TData> extends Omit<AgGridReactProps<TData>, HandledProps | 'onCellClicked' | 'onCellKeyDown'> {
    columnStateKey?: string;
    setColumnState?: (state: ColumnState[]) => void;
    onGridReady?: (params: GridReadyEvent<TData>) => void;
    /**
     * rowData 최초 렌더 완료 시 부모(Table 등)에서 추가 로직을 실행할 때 사용.
     * 내부 스크롤 복원 이후에 호출됩니다.
     */
    onFirstDataRendered?: (params: FirstDataRenderedEvent<TData>) => void;
    onRowClicked?: (event: RowClickedEvent<TData>) => void;
    onCellClicked?: (params: CellClickedEvent<TData>) => void;
    onCellKeyDown?: (params: CellKeyDownEvent<TData>) => void;
    onCellDoubleClicked?: (params: CellDoubleClickedEvent<TData>) => void;
    onPostSortRows?: (params: PostSortRowsParams<TData>) => void;
    onColumnMoved?: () => void;
    onColumnPinned?: () => void;
    onColumnVisible?: () => void;
    onColumnResized?: () => void;
    disableBuiltinCellSelection?: boolean;
    /** @deprecated 컬럼 정의의 pasteable 속성으로 대체됨 */
    pasteableFields?: string[];
    shiftRangeSelection?: ShiftRangeSelectionConfig<TData>; // Shift+클릭 범위 선택 설정
    scrollTop?: number | null;
    setScrollTop?: (top: number | null) => void;
    restoreScroll?: boolean;
    /** localStorage 없을 때 onGridReady에서 1회 적용할 기본 columnState */
    getDefaultColumnState?: () => ColumnState[];
}

function GridExcel<TData extends {} = any>(
    props: GridExcelProps<TData>,
    ref: ForwardedRef<AgGridReact<TData>>
): ReactElement {
    // Grid_Excel 전용 prop + 나머지는 AgGridReact에 그대로 전달(...restProps)
    const {
        columnStateKey,
        setColumnState,
        onGridReady: onGridReadyProp,
        onFirstDataRendered,
        onRowClicked: onRowClickedProp,
        onCellClicked: handleCellClickProp,
        onCellKeyDown: handleCellKeyDownProp,
        onCellDoubleClicked: handleCellDoubleClickProp,
        onPostSortRows: onPostSortRowsProp,
        onColumnMoved: onColumnMovedProp,
        onColumnPinned: onColumnPinnedProp,
        onColumnVisible: onColumnVisibleProp,
        onColumnResized: onColumnResizedProp,
        disableBuiltinCellSelection = false,
        pasteableFields: _deprecated,
        shiftRangeSelection,
        scrollTop,
        setScrollTop,
        restoreScroll = true,
        getDefaultColumnState,
        animateRows = true,
        ...restProps
    } = props;

    const gridRef = useRef<AgGridReact<TData>>(null);

    /**
     * columnDefs 중 pasteable: true 인 컬럼의 field 목록
     */
    const pasteableFields = useMemo(() => {
        if (!props.columnDefs) return [];
        return (props.columnDefs as RaonColDef<TData>[])
            .filter(col => col.pasteable === true && col.field)
            .map(col => col.field!);
    }, [props.columnDefs]);

    /**
     * 컬럼 정의에 suppressKeyboardEvent 주입
     * - 편집 중 Ctrl+V → false (브라우저 네이티브 허용)
     *   단, onCellKeyDown에서 여러 행/열이면 stopEditing 후 executePaste 처리
     * - 비편집 Ctrl+V → false 유지 (onCellKeyDown에서 executePaste 처리)
     *   (true 반환 시 onCellKeyDown 자체가 호출 안 됨)
     */
    const injectedColumnDefs = useMemo(() => {
        if (!props.columnDefs) return props.columnDefs;
        return (props.columnDefs as RaonColDef<TData>[]).map(col => {
            const hasSuppressKeyboard = !!col.suppressKeyboardEvent;
            return {
                ...col,
                suppressKeyboardEvent: (params: any) => {
                    const isCtrlV = (params.event.ctrlKey || params.event.metaKey) && params.event.key === 'v';
                    if (isCtrlV) {
                        // 편집 중/아님 모두 false → onCellKeyDown에서 처리
                        return false;
                    }
                    // agSelectCellEditor 편집 중 위/아래 화살표는 그리드 네비게이션이 아닌
                    // 드롭다운 옵션 이동에 사용되도록 기본 그리드 키처리를 막습니다.
                    const isArrowUpDown = params.event.key === 'ArrowUp' || params.event.key === 'ArrowDown';
                    const isSelectEditor = col.cellEditor === 'agSelectCellEditor';
                    const cellEditor = col.cellEditor as any;
                    const isCustomDropdownColumn =
                        cellEditor === GridDropDownForm ||
                        cellEditor?.displayName === GridDropDownForm.displayName;
                    const isCustomDropdownEditor = (params.event.target as HTMLElement | null)
                        ?.closest?.('.ag-custom-dropdown-editor');
                    if (params.editing && isArrowUpDown && (isSelectEditor || isCustomDropdownColumn || isCustomDropdownEditor)) {
                        return true;
                    }
                    return hasSuppressKeyboard ? col.suppressKeyboardEvent!(params) : false;
                }
            };
        });
    }, [props.columnDefs]);

    /**
     * 셀 클릭/키다운으로 범위 선택 등 처리 — injectedColumnDefs 기반으로 colDef 가공(processedColDefs)
     */
    const {
        anchorCellRef,
        selectedCellRef,
        handleCellClick_targeting,
        handleCellCopy,
        handleCellMouseDown,
        handleCellMouseOver,
        clearRange,
		getSelectedCellRange,
        processedColDefs,
    } = useGridCellSelection({
        columnDefs: disableBuiltinCellSelection ? null : injectedColumnDefs,
    });

    useImperativeHandle(
        ref,
        () => {
            const inst = gridRef.current;
            if (!inst) return null as unknown as AgGridReact<TData>;
            return Object.assign(inst, {
                clearRange: () => {
                    const api = gridRef.current?.api;
                    if (api) clearRange(api);
                },
				getSelectedCellRange,
            }) as AgGridReact<TData>;
        },
		[clearRange, getSelectedCellRange]
    );

    /** 컬럼 이동/핀/숨김/리사이즈 시 localStorage 등에 컬럼 상태 저장 */
    const saveColumnState = columnStateKey && setColumnState
        ? createSaveColumnState(gridRef, columnStateKey, setColumnState)
        : undefined;

    /** 해당 셀(colDef + 이벤트 params)이 편집 가능한지 — editable 함수/불리언 반영 */
    const isCellEditable = (colDef: ColDef<TData>, params: CellClickedEvent<TData> | CellKeyDownEvent<TData>): boolean => {
        if (typeof colDef.editable === 'function') {
            const editableParams: EditableCallbackParams<TData> = {
                node: params.node,
                data: params.data,
                colDef: colDef,
                column: params.column,
                api: params.api,
                context: params.context,
            };
            return colDef.editable(editableParams);
        }
        return !!colDef.editable;
    };

    /** 그리드 준비 완료: 셀 선택용 context 주입, 컬럼 상태 복원, 사용자 onGridReady 호출 */
    const handleGridReady = (params: GridReadyEvent<TData>) => {
        if (!disableBuiltinCellSelection) {
            params.api.setGridOption('context', {
                ...restProps.context,
                cellSelectionRef: selectedCellRef,
            });
        }
        if (columnStateKey && setColumnState) {
            onGridReady(gridRef, columnStateKey, setColumnState, getDefaultColumnState);
        }
        if (onGridReadyProp) onGridReadyProp(params);
    };

    // 사용자 스크롤 종료 시 현재 위치를 Store에 저장 (탭 전환 후 복원용)
    const handleBodyScrollEnd = useCallback(() => {
        const api = gridRef.current?.api;
        if (api && setScrollTop) {
            saveGridScrollState(api, setScrollTop);
        }
    }, [setScrollTop]);

    const { setLoadingSpinner } = useLoadingSpinnerStore();

    // rowData 최초 렌더 완료 후 Store에 저장된 스크롤 위치 복원 (탭 복귀·재마운트 시)
    const handleFirstDataRendered = useCallback((params: FirstDataRenderedEvent<TData>) => {
        if (restoreScroll && setScrollTop) {
            const shouldRestoreScroll = scrollTop != null && scrollTop >= 0;
            if (shouldRestoreScroll) {
                withScrollRestoreLoading(setLoadingSpinner, (onComplete) => {
                    restoreGridScrollState(params.api, scrollTop, onComplete);
                });
            } else {
                restoreGridScrollState(params.api, scrollTop);
            }
        }
        if (onFirstDataRendered) {
            onFirstDataRendered(params);
        }
    }, [restoreScroll, setScrollTop, scrollTop, onFirstDataRendered, setLoadingSpinner]);

    /**
     * 셀 클릭: 편집 가능하면 부모 handleCellClickProp, 아니면 내장 셀 타겟팅(범위 선택)
     */
    const onCellClicked = (params: CellClickedEvent<TData>) => {
        const isEditable = isCellEditable(params.colDef, params);
        if (handleCellClickProp && isEditable) {
            handleCellClickProp(params);
        } else if (!disableBuiltinCellSelection) {
            handleCellClick_targeting(params);
        }
    };

    /**
     * 엑셀 붙여넣기 기능
	 * 엑셀 클립보드 텍스트를 탭/줄 단위로 파싱해 그리드에 반영 (행 붙여넣기 전용)
     */
    const executePaste = (api: any, focusedCell: any, text: string) => {
        const pastedRows = text
            .replace(/\r\n/g, '\n')
            .replace(/\r/g, '\n')
            .trimEnd()
            .split('\n')
            .map((row: string) => row.split('\t'));

        const allColumns = api.getAllDisplayedColumns();
        if (!allColumns) return;

        const startRowIndex = focusedCell.rowIndex;
        const startColIndex = allColumns.findIndex((col: any) => col === focusedCell.column);
        //  여기 추가해서 실제 값 확인
        // console.log('=== PASTE DEBUG ===');
        // console.log('focusedCell.column colId:', focusedCell.column?.getColId?.());
        // console.log('startColIndex:', startColIndex);
        // console.log('allColumns colIds:', allColumns.map((c: any) => c.getColId()));
        // console.log('pastedRows:', text.split('\n').map(r => r.split('\t')));

        if (startColIndex === -1) {
            console.log('❌ startColIndex -1 → paste 실패');
            return;
        }

        pastedRows.forEach((rowData: string[], rowOffset: number) => {
            rowData.forEach((cellValue: string, colOffset: number) => {
                const targetRowIndex = startRowIndex + rowOffset;
                const targetColIndex = startColIndex + colOffset;
                if (targetColIndex >= allColumns.length) return;

                const rowNode = api.getDisplayedRowAtIndex(targetRowIndex);
                if (!rowNode || rowNode.rowPinned) return;

                const column = allColumns[targetColIndex];
                const colDef = column.getColDef() as RaonColDef<TData>;
                const targetField = colDef.field;
                if (!targetField) return;

                const isPasteable = colDef.pasteable === true;
                const isEditable = isPasteable || (
                    typeof colDef.editable === 'function'
                        ? colDef.editable({ node: rowNode, data: rowNode.data, colDef, column, api, context: api.getGridOption('context') } as any)
                        : !!colDef.editable
                );

                if (!isEditable) return;

                const cleanValue = cellValue.replace(/,/g, '').trim();

                let finalValue: any = cleanValue;
                if (colDef.valueParser) {
                    finalValue = (colDef.valueParser as Function)({
                        newValue: cleanValue,
                        oldValue: rowNode.data?.[targetField as unknown as keyof TData],
                        data: rowNode.data,
                        node: rowNode,
                        colDef,
                        column,
                        api,
                        context: api.getGridOption('context'),
                    });
                }

                rowNode.setDataValue(targetField, finalValue);
            });
        });
    };

    /**
     * 클립보드 데이터가 여러 행/열인지 확인
     */
    const isMultiCellData = (text: string): boolean => {
        const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trimEnd();
        const rows = normalized.split('\n');
        // 여러 행이거나 탭 구분(여러 열)이 있으면 true
        return rows.length > 1 || rows.some(r => r.includes('\t'));
    };

    /**
     * 키다운 처리
     * 클립보드가 여러 행/열 → 편집 중지 후 executePaste (행 붙여넣기)
     * 클립보드가 단일 값   → 편집 중이면 return (브라우저 네이티브 paste)
     *                          편집 중 아니면 executePaste (단일 셀 붙여넣기)
     */
    const onCellKeyDown = (params: CellKeyDownEvent<TData>) => {
        const event = params.event as KeyboardEvent;

		// 엑셀 복사 기능
        // 편집 중 Ctrl+C는 에디터의 텍스트 복사에 맡기고, 비편집 상태에서만 셀 범위를 복사합니다.
        if ((event.ctrlKey || event.metaKey) && event.key === 'c') {
            if (params.api.getEditingCells().length > 0) {
                return;
            }

            if (!disableBuiltinCellSelection) {
                handleCellCopy(params);
                return;
            }
        }

		// 엑셀 붙여넣기 기능
        if ((event.ctrlKey || event.metaKey) && event.key === 'v' && pasteableFields.length > 0) {
            const field = params.colDef.field;
            if (field && pasteableFields.includes(field)) {

                const api = params.api;
                const editingCells = api.getEditingCells();

                const anchor = anchorCellRef.current;
                const focusedCell = anchor
                    ? {
                        rowIndex: anchor.rowIndex,
                        column: params.api.getAllDisplayedColumns()
                            .find((col: any) => col.getColId() === anchor.colId) ?? params.column,
                    }
                    : {
                        rowIndex: params.node.rowIndex!,
                        column: params.column,
                    };

                navigator.clipboard.readText().then(text => {
                    if (!text) return;

                    if (isMultiCellData(text)) {
                        // 여러 행/열 → 무조건 행 붙여넣기
                        // 편집 중이면 편집 취소 후 처리
                        if (editingCells.length > 0) {
                            api.stopEditing(true); // true: 변경사항 취소
                        }
                        executePaste(api, focusedCell, text);
                    } else {
                        // 단일 값 → 편집 중이면 브라우저 네이티브에 맡김
                        if (editingCells.length > 0) {
                            return; // 브라우저가 커서 위치에 붙여넣기
                        }
                        executePaste(api, focusedCell, text);
                    }
                }).catch(() => {});

                // 비편집 상태에서만 preventDefault (편집 중엔 네이티브 허용)
                if (editingCells.length === 0) {
                    event.preventDefault();
                    event.stopPropagation();
                }

                return;
            }
        }

        const isEditable = isCellEditable(params.colDef, params);
        if (handleCellKeyDownProp && isEditable) {
            handleCellKeyDownProp(params);
        } else if (!disableBuiltinCellSelection) {
            handleCellCopy(params);
        }
    };

    /** 컬럼 레이아웃 변경 시 상태 저장 + 부모 콜백 */
    const handleColumnMoved = () => { saveColumnState?.(); onColumnMovedProp?.(); };
    const handleColumnPinned = () => { saveColumnState?.(); onColumnPinnedProp?.(); };
    const handleColumnVisible = () => { saveColumnState?.(); onColumnVisibleProp?.(); };
    const handleColumnResized = () => { saveColumnState?.(); onColumnResizedProp?.(); };

    // Shift+클릭 범위 선택 처리
    const onRowClicked = useShiftRangeRowSelection<TData>({
        config: shiftRangeSelection,
        onRowClicked: onRowClickedProp,
    });

    return (
        <div className="flex-1 ag-theme-alpine w-full overflow-y-auto relative">
            <AgGridReact<TData>
                ref={gridRef}
                overlayNoRowsTemplate={'<span style="padding: 32px;">조회된 데이터가 없습니다.</span>'}
                {...restProps}
                popupParent={restProps.popupParent ?? (typeof document !== 'undefined' ? document.body : undefined)}
                enableRangeSelection={true}
                suppressClipboardPaste={true}
                columnDefs={disableBuiltinCellSelection ? injectedColumnDefs : processedColDefs}
                onCellClicked={onCellClicked}
                onCellMouseDown={disableBuiltinCellSelection ? undefined : handleCellMouseDown}
                onCellMouseOver={disableBuiltinCellSelection ? undefined : handleCellMouseOver}
                onCellKeyDown={onCellKeyDown}
                onCellDoubleClicked={handleCellDoubleClickProp}
                postSortRows={onPostSortRowsProp}
                onRowClicked={onRowClicked}
                animateRows={animateRows}
                stopEditingWhenCellsLoseFocus={true}
                suppressScrollOnNewData={true}
                suppressColumnVirtualisation={true}
                suppressColumnMoveAnimation={true}
                getRowStyle={getRowStyle}
                onGridReady={handleGridReady}
                onFirstDataRendered={handleFirstDataRendered}
                onBodyScrollEnd={handleBodyScrollEnd}
                onColumnMoved={handleColumnMoved}
                onColumnPinned={handleColumnPinned}
                onColumnVisible={handleColumnVisible}
                onColumnResized={handleColumnResized}
            />
        </div>
    );
}

/** forwardRef로 ref 전달 가능한 그리드 컴포넌트 */
const Grid_Excel = forwardRef(GridExcel) as <TData extends {} = any>(
    props: GridExcelProps<TData> & { ref?: ForwardedRef<AgGridReact<TData>> }
) => ReactElement;

(Grid_Excel as React.FunctionComponent).displayName = "Grid_Excel";

export default Grid_Excel;
